<template>
  <div class="pay-success-page">
    <main class="pay-success-shell" aria-labelledby="pay-success-title">
      <section class="success-hero">
        <div class="confetti-layer" aria-hidden="true">
          <span class="confetti confetti-1"></span>
          <span class="confetti confetti-2"></span>
          <span class="confetti confetti-3"></span>
          <span class="confetti confetti-4"></span>
          <span class="confetti confetti-5"></span>
          <span class="confetti confetti-6"></span>
        </div>

        <div class="success-icon" aria-hidden="true">
          <font-awesome-icon icon="check" />
        </div>

        <h1 id="pay-success-title" class="success-title">支付成功</h1>
        <p class="success-desc">感谢您的支付，祝您生活愉快！</p>
      </section>

      <section class="order-card" aria-label="订单信息">
        <div class="amount-block">
          <span class="amount-label">支付金额</span>
          <strong class="amount-value">
            <small>¥</small>
            {{ displayAmount }}
          </strong>
        </div>

        <dl class="order-list">
          <div class="order-row">
            <dt>订单编号</dt>
            <dd>{{ orderNo }}</dd>
          </div>
          <div class="order-row">
            <dt>支付方式</dt>
            <dd class="payment-method">
              <span class="payment-method-icon" :class="paymentMethodClass" aria-hidden="true">
                <font-awesome-icon :icon="paymentMethodIcon" />
              </span>
              {{ paymentMethodName }}
            </dd>
          </div>
          <div class="order-row">
            <dt>支付时间</dt>
            <dd>{{ payTime }}</dd>
          </div>
        </dl>
      </section>

      <section class="action-group" aria-label="页面操作">
        <button type="button" class="secondary-action" @click="handleBack">
          返回
        </button>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const methodMap = {
  wechat: {
    name: '微信支付',
    icon: ['fab', 'weixin'],
    className: 'wechat'
  },
  alipay: {
    name: '支付宝',
    icon: ['fab', 'alipay'],
    className: 'alipay'
  }
} as const

const getQueryText = (key: string, fallback: string) => {
  const value = route.query[key]

  if (Array.isArray(value)) {
    return value[0] || fallback
  }

  return value || fallback
}

const displayAmount = computed(() => {
  const rawAmount = getQueryText('total_amount', getQueryText('amount', '0.00'))
  const amount = Number(rawAmount)

  if (!Number.isFinite(amount)) {
    return rawAmount
  }

  return amount.toFixed(2)
})

const orderNo = computed(() =>
  getQueryText('orderNo', getQueryText('out_trade_no', getQueryText('orderId', '')))
)

const payTime = computed(() => getQueryText('timestamp', getQueryText('payTime', '')))

const paymentMethod = computed(() => {
  const method = getQueryText('paymentMethod', getQueryText('method', 'alipay')).toLowerCase()
  if (method.includes('alipay')) return methodMap.alipay
  if (method.includes('wechat')) return methodMap.wechat
  return method in methodMap ? methodMap[method as keyof typeof methodMap] : methodMap.alipay
})

const paymentMethodName = computed(() => paymentMethod.value.name)
const paymentMethodIcon = computed(() => paymentMethod.value.icon)
const paymentMethodClass = computed(() => `payment-method-icon-${paymentMethod.value.className}`)

const handleBack = () => {
  const target = getQueryText('orderTarget', '/vip')
  router.push(target)
}
</script>

<style scoped>
.pay-success-page {
  --page-cream: #fdfbf7;
  --page-cream-deep: #f5f0e8;
  --page-cream-soft: #faf8f5;
  --brand-orange: #ff8a3d;
  --brand-orange-deep: #ff6b35;
  --brand-amber: #ffc88e;
  --success-green: #24c66a;
  --ink-strong: #2b2927;
  --ink-muted: #8d7c6d;

  min-height: 100vh;
  padding: max(28px, env(safe-area-inset-top, 0px)) 18px max(24px, env(safe-area-inset-bottom, 0px));
  background:
    radial-gradient(circle at 50% 8%, rgba(255, 200, 142, 0.34), rgba(255, 200, 142, 0) 31%),
    radial-gradient(circle at 14% 28%, rgba(255, 138, 61, 0.12), rgba(255, 138, 61, 0) 28%),
    radial-gradient(circle at 88% 20%, rgba(93, 190, 120, 0.13), rgba(93, 190, 120, 0) 25%),
    linear-gradient(135deg, var(--page-cream) 0%, var(--page-cream-deep) 52%, var(--page-cream-soft) 100%);
  color: var(--ink-strong);
}

.pay-success-shell {
  width: min(365px, 100%);
  margin: 0 auto;
}

.success-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 195px;
  padding-top: 19px;
  text-align: center;
}

.confetti-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.confetti {
  position: absolute;
  display: block;
  border-radius: 999px;
}

.confetti-1 {
  width: 7px;
  height: 7px;
  left: 27%;
  top: 37px;
  background: #6fcf86;
}

.confetti-2 {
  width: 6px;
  height: 11px;
  left: 35%;
  top: 6px;
  border-radius: 3px;
  background: var(--brand-amber);
  transform: rotate(-16deg);
}

.confetti-3 {
  width: 8px;
  height: 17px;
  right: 25%;
  top: 1px;
  background: #8edfa7;
  transform: rotate(38deg);
  clip-path: polygon(0 55%, 100% 0, 70% 70%, 0 100%);
}

.confetti-4 {
  width: 19px;
  height: 9px;
  left: 30%;
  top: 86px;
  background: #5ba7d8;
  transform: rotate(-33deg);
  clip-path: polygon(0 100%, 18% 12%, 100% 0, 84% 58%, 35% 70%);
}

.confetti-5 {
  width: 19px;
  height: 14px;
  right: 27%;
  top: 52px;
  background: var(--brand-orange);
  transform: rotate(-42deg);
  clip-path: polygon(0 100%, 0 42%, 42% 0, 100% 0, 100% 32%, 48% 32%);
}

.confetti-6 {
  width: 6px;
  height: 6px;
  right: 18%;
  top: 88px;
  background: #6da9db;
}

.success-icon {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 43px;
  background: linear-gradient(145deg, #57db83 0%, var(--success-green) 100%);
  box-shadow:
    0 0 0 10px rgba(255, 226, 192, 0.74),
    0 18px 34px rgba(255, 138, 61, 0.18),
    0 16px 30px rgba(36, 198, 106, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.34);
}

.success-title {
  margin: 25px 0 0;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 900;
  letter-spacing: 0;
}

.success-desc {
  margin: 14px 0 0;
  color: var(--ink-muted);
  font-size: 14px;
  line-height: 1.4;
  font-weight: 700;
}

.order-card {
  overflow: hidden;
  margin-top: 6px;
  padding: 27px 18px 20px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 253, 249, 0.94) 100%);
  box-shadow:
    0 18px 42px rgba(140, 104, 68, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.amount-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 19px;
  border-bottom: 1px solid rgba(216, 193, 168, 0.42);
}

.amount-label {
  color: #9a8776;
  font-size: 14px;
  font-weight: 700;
}

.amount-value {
  margin-top: 8px;
  color: var(--ink-strong);
  font-size: 34px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}

.amount-value small {
  margin-right: 8px;
  font-size: 21px;
  font-weight: 900;
}

.order-list {
  margin: 0;
  padding-top: 16px;
}

.order-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 36px;
}

.order-row + .order-row {
  margin-top: 1px;
}

.order-row dt {
  flex: 0 0 auto;
  color: #9a8776;
  font-size: 14px;
  font-weight: 700;
}

.order-row dd {
  min-width: 0;
  margin: 0;
  color: #443c35;
  font-size: 14px;
  font-weight: 700;
  text-align: right;
  overflow-wrap: anywhere;
}

.payment-method {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
}

.payment-method-icon {
  width: 19px;
  height: 19px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
}

.payment-method-icon-wechat {
  background: #19c83f;
}

.payment-method-icon-alipay {
  background: #1677ff;
}

.action-group {
  display: grid;
  gap: 12px;
  margin-top: 19px;
  padding: 0 52px;
}

.primary-action,
.secondary-action {
  width: 100%;
  min-height: 42px;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.primary-action {
  border: none;
  color: #fff;
  background: linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-orange-deep) 100%);
  box-shadow: 0 12px 24px rgba(255, 107, 53, 0.24);
}

.secondary-action {
  border: none;
  color: #fff;
  background: linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-orange-deep) 100%);
  box-shadow:
    0 14px 28px rgba(255, 107, 53, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.26);
}

.primary-action:hover,
.secondary-action:hover {
  transform: translateY(-1px);
}

.primary-action:hover {
  box-shadow: 0 16px 28px rgba(255, 107, 53, 0.3);
}

.secondary-action:hover {
  box-shadow:
    0 18px 32px rgba(255, 107, 53, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.primary-action:active,
.secondary-action:active {
  transform: translateY(0);
}

@media (min-width: 768px) {
  .pay-success-page {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }

  .pay-success-shell {
    width: 395px;
  }
}

@media (max-width: 360px) {
  .action-group {
    padding: 0 34px;
  }

  .amount-value {
    font-size: 31px;
  }

  .order-row {
    gap: 12px;
  }
}
</style>
